/*
  SOTE — Australia delivery estimator
  ------------------------------------
  Renders inside snippets/sote-au-delivery.liquid for visitors browsing the Australia market.

  What it does
    • Validates a 4-digit Australian postcode and maps it to a state + service tier
      (metro / regional / remote) using Australia Post postcode ranges.
    • Works out the dispatch date from the US warehouse (Robo3PL, Utah) using the
      same 4pm Eastern weekday cutoff the bundle builder already uses.
    • Adds the configured transit window in AU business days (weekends + AU public
      holidays for that state skipped) and prints an AU-formatted date range.
    • Remembers the postcode in localStorage so it pre-fills across pages.

  All numbers (transit days, cutoff, extra days for regional/remote) come from
  data-attributes set by the snippet, which reads them from Theme settings → Australia market.
*/
(function () {
  'use strict';
  if (window.SoteAuDelivery) { return; }

  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var STORAGE_KEY = 'sote_au_postcode';

  /* ---------------------------------------------------------------
     Postcode → state + tier
     Order matters: first matching range wins, so the more specific
     metro / remote ranges sit above the broad state ranges.
     --------------------------------------------------------------- */
  var ZONES = [
    // External territories & islands (remote)
    [2898, 2898, 'NSW', 'remote', 'Lord Howe Island'],
    [2899, 2899, 'NSW', 'remote', 'Norfolk Island'],
    [6798, 6798, 'WA', 'remote', 'Christmas Island'],
    [6799, 6799, 'WA', 'remote', 'Cocos (Keeling) Islands'],
    [7255, 7256, 'TAS', 'remote', 'Bass Strait islands'],
    [4875, 4875, 'QLD', 'remote', 'Torres Strait'],

    // ACT
    [200, 299, 'ACT', 'metro', 'Canberra'],
    [2600, 2618, 'ACT', 'metro', 'Canberra'],
    [2900, 2920, 'ACT', 'metro', 'Canberra'],

    // NT
    [800, 832, 'NT', 'metro', 'Darwin'],
    [833, 999, 'NT', 'remote', 'remote NT'],

    // NSW
    [1000, 1999, 'NSW', 'metro', 'Sydney'],
    [2000, 2249, 'NSW', 'metro', 'Sydney'],
    [2250, 2263, 'NSW', 'metro', 'Central Coast'],
    [2280, 2330, 'NSW', 'metro', 'Newcastle & Hunter'],
    [2500, 2530, 'NSW', 'metro', 'Wollongong'],
    [2555, 2574, 'NSW', 'metro', 'Sydney'],
    [2740, 2786, 'NSW', 'metro', 'Sydney'],
    [2870, 2897, 'NSW', 'remote', 'far west NSW'],
    [2000, 2999, 'NSW', 'regional', 'regional NSW'],

    // VIC
    [3000, 3207, 'VIC', 'metro', 'Melbourne'],
    [3211, 3220, 'VIC', 'metro', 'Geelong'],
    [3226, 3227, 'VIC', 'metro', 'Geelong'],
    [3335, 3341, 'VIC', 'metro', 'Melbourne'],
    [3427, 3443, 'VIC', 'metro', 'Melbourne'],
    [3750, 3811, 'VIC', 'metro', 'Melbourne'],
    [3910, 3920, 'VIC', 'metro', 'Mornington Peninsula'],
    [3926, 3944, 'VIC', 'metro', 'Mornington Peninsula'],
    [3975, 3981, 'VIC', 'metro', 'Melbourne'],
    [8000, 8999, 'VIC', 'metro', 'Melbourne'],
    [3000, 3999, 'VIC', 'regional', 'regional VIC'],

    // QLD
    [4000, 4207, 'QLD', 'metro', 'Brisbane'],
    [4208, 4230, 'QLD', 'metro', 'Gold Coast'],
    [4270, 4275, 'QLD', 'metro', 'Gold Coast'],
    [4300, 4306, 'QLD', 'metro', 'Ipswich'],
    [4500, 4521, 'QLD', 'metro', 'Brisbane'],
    [4550, 4575, 'QLD', 'metro', 'Sunshine Coast'],
    [9000, 9999, 'QLD', 'metro', 'Brisbane'],
    [4880, 4899, 'QLD', 'remote', 'far north QLD'],
    [4000, 4999, 'QLD', 'regional', 'regional QLD'],

    // SA
    [5000, 5174, 'SA', 'metro', 'Adelaide'],
    [5231, 5235, 'SA', 'metro', 'Adelaide Hills'],
    [5720, 5799, 'SA', 'remote', 'outback SA'],
    [5800, 5999, 'SA', 'metro', 'Adelaide'],
    [5000, 5999, 'SA', 'regional', 'regional SA'],

    // WA
    [6000, 6214, 'WA', 'metro', 'Perth'],
    [6900, 6999, 'WA', 'metro', 'Perth'],
    [6400, 6797, 'WA', 'remote', 'regional & remote WA'],
    [6000, 6999, 'WA', 'regional', 'regional WA'],

    // TAS
    [7000, 7099, 'TAS', 'metro', 'Hobart'],
    [7248, 7278, 'TAS', 'metro', 'Launceston'],
    [7800, 7999, 'TAS', 'metro', 'Hobart'],
    [7000, 7999, 'TAS', 'regional', 'regional TAS']
  ];

  function classify(postcode) {
    var n = parseInt(postcode, 10);
    if (isNaN(n)) { return null; }
    for (var i = 0; i < ZONES.length; i++) {
      var z = ZONES[i];
      if (n >= z[0] && n <= z[1]) {
        return { postcode: postcode, state: z[2], tier: z[3], label: z[4] };
      }
    }
    return null;
  }

  function isValidPostcode(value) {
    if (!/^\d{4}$/.test(value)) { return false; }
    return classify(value) !== null;
  }

  /* ---------------------------------------------------------------
     Date helpers (all dates handled as UTC-midnight "calendar days")
     --------------------------------------------------------------- */
  function ymd(y, m, d) { return Date.UTC(y, m, d); }
  function addDays(t, n) { return t + n * 86400000; }
  function dow(t) { return new Date(t).getUTCDay(); }
  function nthWeekday(y, m, weekday, n) { // n-th (1-based) weekday of month
    var t = ymd(y, m, 1);
    var offset = (weekday - dow(t) + 7) % 7;
    return addDays(t, offset + (n - 1) * 7);
  }
  function lastWeekday(y, m, weekday) {
    var t = ymd(y, m + 1, 0); // last day of month
    var offset = (dow(t) - weekday + 7) % 7;
    return addDays(t, -offset);
  }
  function easterSunday(y) { // Anonymous Gregorian algorithm
    var a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    var f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return ymd(y, month, day);
  }
  function observed(t) { // weekend public holidays roll to Monday
    var d = dow(t);
    if (d === 6) { return addDays(t, 2); }
    if (d === 0) { return addDays(t, 1); }
    return t;
  }

  var holidayCache = {};
  function auHolidays(year, state) {
    var key = year + state;
    if (holidayCache[key]) { return holidayCache[key]; }
    var e = easterSunday(year);
    var list = [
      observed(ymd(year, 0, 1)),        // New Year's Day
      observed(ymd(year, 0, 26)),       // Australia Day
      addDays(e, -2),                   // Good Friday
      addDays(e, 1),                    // Easter Monday
      ymd(year, 3, 25),                 // Anzac Day (not generally substituted)
      ymd(year, 11, 25),                // Christmas Day
      ymd(year, 11, 26)                 // Boxing Day
    ];
    // Christmas / Boxing Day substitutes
    if (dow(ymd(year, 11, 25)) === 6) { list.push(ymd(year, 11, 27)); }
    if (dow(ymd(year, 11, 25)) === 0) { list.push(ymd(year, 11, 27)); }
    if (dow(ymd(year, 11, 26)) === 6) { list.push(ymd(year, 11, 28)); }
    if (dow(ymd(year, 11, 26)) === 0) { list.push(ymd(year, 11, 28)); }
    // King's Birthday
    if (state === 'QLD') { list.push(nthWeekday(year, 9, 1, 1)); }
    else if (state === 'WA') { list.push(lastWeekday(year, 8, 1)); }
    else { list.push(nthWeekday(year, 5, 1, 2)); }
    // Labour Day
    if (state === 'WA') { list.push(nthWeekday(year, 2, 1, 1)); }
    else if (state === 'VIC' || state === 'TAS') { list.push(nthWeekday(year, 2, 1, 2)); }
    else if (state === 'QLD' || state === 'NT') { list.push(nthWeekday(year, 4, 1, 1)); }
    else { list.push(nthWeekday(year, 9, 1, 1)); } // NSW, ACT, SA
    holidayCache[key] = list;
    return list;
  }

  function usHolidays(year) {
    return [
      observed(ymd(year, 0, 1)),        // New Year's Day
      lastWeekday(year, 4, 1),          // Memorial Day
      observed(ymd(year, 6, 4)),        // Independence Day
      nthWeekday(year, 8, 1, 1),        // Labor Day
      nthWeekday(year, 10, 4, 4),       // Thanksgiving
      addDays(nthWeekday(year, 10, 4, 4), 1), // Day after Thanksgiving
      observed(ymd(year, 11, 25))       // Christmas Day
    ];
  }

  function isBusinessDay(t, holidays) {
    var d = dow(t);
    if (d === 0 || d === 6) { return false; }
    for (var i = 0; i < holidays.length; i++) { if (holidays[i] === t) { return false; } }
    return true;
  }

  function addBusinessDays(t, n, holidayFn) {
    var cur = t;
    while (n > 0) {
      cur = addDays(cur, 1);
      if (isBusinessDay(cur, holidayFn(new Date(cur).getUTCFullYear()))) { n--; }
    }
    return cur;
  }

  function nextBusinessDayOrSame(t, holidayFn) {
    var cur = t;
    while (!isBusinessDay(cur, holidayFn(new Date(cur).getUTCFullYear()))) { cur = addDays(cur, 1); }
    return cur;
  }

  /* Current date/time in the warehouse timezone (America/New_York) */
  function warehouseNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour12: false,
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric'
    }).formatToParts(new Date());
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    var hour = parseInt(o.hour, 10); if (hour === 24) { hour = 0; }
    return { day: ymd(parseInt(o.year, 10), parseInt(o.month, 10) - 1, parseInt(o.day, 10)), hour: hour };
  }

  function dispatchDate(cfg) {
    var now = warehouseNow();
    var day = now.day;
    var todayOk = isBusinessDay(day, usHolidays(new Date(day).getUTCFullYear())) && now.hour < cfg.cutoffHour;
    if (!todayOk) { day = nextBusinessDayOrSame(addDays(day, 1), usHolidays); }
    if (cfg.handlingDays > 0) { day = addBusinessDays(day, cfg.handlingDays, usHolidays); }
    return { day: day, isToday: day === now.day };
  }

  function fmt(t, withYear) {
    var d = new Date(t);
    var s = DAY_NAMES[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTH_NAMES[d.getUTCMonth()];
    if (withYear) { s += ' ' + d.getUTCFullYear(); }
    return s;
  }

  function estimate(zone, cfg) {
    var dispatch = dispatchDate(cfg);
    var extra = zone.tier === 'regional' ? cfg.regionalExtra : (zone.tier === 'remote' ? cfg.remoteExtra : 0);
    var minDays = cfg.minDays + extra;
    var maxDays = cfg.maxDays + extra;
    var hol = function (y) { return auHolidays(y, zone.state); };
    var from = addBusinessDays(dispatch.day, minDays, hol);
    var to = addBusinessDays(dispatch.day, maxDays, hol);
    var crossYear = new Date(from).getUTCFullYear() !== new Date(to).getUTCFullYear();
    return {
      dispatch: dispatch,
      from: from,
      to: to,
      minDays: minDays,
      maxDays: maxDays,
      rangeText: fmt(from, crossYear) + ' – ' + fmt(to, crossYear)
    };
  }

  /* ---------------------------------------------------------------
     DOM wiring
     --------------------------------------------------------------- */
  function readConfig(root) {
    var ds = root.dataset;
    return {
      minDays: parseInt(ds.minDays, 10) || 6,
      maxDays: parseInt(ds.maxDays, 10) || 10,
      regionalExtra: parseInt(ds.regionalExtra, 10) || 0,
      remoteExtra: parseInt(ds.remoteExtra, 10) || 0,
      cutoffHour: parseInt(ds.cutoffHour, 10) || 16,
      handlingDays: parseInt(ds.handlingDays, 10) || 0
    };
  }

  function getStored() {
    try { return window.localStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
  }
  function setStored(v) {
    try { window.localStorage.setItem(STORAGE_KEY, v); } catch (e) { /* private mode */ }
  }

  function renderDispatch(root, cfg) {
    var el = root.querySelector('[data-au-dispatch]');
    if (!el) { return; }
    var d = dispatchDate(cfg);
    el.textContent = d.isToday ? 'dispatches today' : 'dispatches ' + fmt(d.day, false);
  }

  function renderResult(root, cfg, postcode) {
    var result = root.querySelector('[data-au-result]');
    var input = root.querySelector('[data-au-input]');
    if (!result) { return; }
    var value = (postcode || '').trim();
    if (!isValidPostcode(value)) {
      root.classList.remove('is-estimated');
      root.classList.add('has-error');
      result.innerHTML = 'Enter a valid 4-digit Australian postcode (e.g. 6000 for Perth).';
      if (input) { input.setAttribute('aria-invalid', 'true'); }
      return;
    }
    var zone = classify(value);
    var est = estimate(zone, cfg);
    root.classList.remove('has-error');
    root.classList.add('is-estimated');
    if (input) { input.removeAttribute('aria-invalid'); }
    result.innerHTML =
      '<span class="sote-au-ship__result-label">Estimated delivery to <strong>' + value + '</strong> (' + zone.label + '):</span> ' +
      '<strong class="sote-au-ship__dates">' + est.rangeText + '</strong>' +
      '<span class="sote-au-ship__days">' + est.minDays + '–' + est.maxDays + ' business days after dispatch</span>';
    setStored(value);
    // Let other widgets on the page (e.g. the bundle builder) reuse the postcode
    document.dispatchEvent(new CustomEvent('sote:au-postcode', { detail: { postcode: value, zone: zone, estimate: est } }));
  }

  function init(root) {
    if (root.dataset.auInit === '1') { return; }
    root.dataset.auInit = '1';
    var cfg = readConfig(root);
    renderDispatch(root, cfg);

    var form = root.querySelector('[data-au-form]');
    var input = root.querySelector('[data-au-input]');
    if (form && input) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        renderResult(root, cfg, input.value);
      });
      input.addEventListener('input', function () {
        input.value = input.value.replace(/\D/g, '').slice(0, 4);
        if (input.value.length === 4) { renderResult(root, cfg, input.value); }
      });
      var stored = getStored();
      if (stored && !input.value) {
        input.value = stored;
        renderResult(root, cfg, stored);
      }
    }
    // Keep several estimators on one page in sync
    document.addEventListener('sote:au-postcode', function (e) {
      if (!input || input.value === e.detail.postcode) { return; }
      input.value = e.detail.postcode;
      renderResult(root, cfg, e.detail.postcode);
    });
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-sote-au-ship]');
    for (var i = 0; i < nodes.length; i++) { init(nodes[i]); }
  }

  window.SoteAuDelivery = { classify: classify, estimate: estimate, init: initAll, isValidPostcode: isValidPostcode };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Theme editor: re-init when sections are re-rendered
  document.addEventListener('shopify:section:load', initAll);
})();
